/**
 * Reno System – Monthly Brief Report
 * -----------------------------------------------------------------------
 * A short management summary, separate from the detailed Balance Sheet:
 * per month (or month slice) — Carry Forward, Total Income, Total Expenses,
 * Balance on Hand, the equation, Detailed Income and Detailed Expenses.
 * No ledger, reconciliation, month close, settings or partner share.
 *
 *   GET /project/{id}/monthly-brief-report?mode=monthly|three_months&year&month
 *   GET /project/{id}/monthly-brief-report?mode=custom&start_date&end_date
 *   GET /project/{id}/monthly-financial-report/transactions?range=1&...   (drill-down, per section)
 *
 * Every figure comes from the backend (MonthlyFinancialReportService) and is
 * normalized once; the screen, print layout and PDF render that same object.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Grid, IconButton, LinearProgress, Skeleton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import PrintIcon from "@mui/icons-material/Print";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useNotifier } from "../../Core/Notifier";
import { apiErrorMessage } from "../BalanceSheet/api";
import TransactionDrilldownDialog from "../BalanceSheet/components/TransactionDrilldownDialog";
import { AttachmentViewer } from "../BalanceSheet/components/AttachmentViewer";
import { BreakdownRow, DrilldownTarget, PeriodSelection } from "../BalanceSheet/types";
import { getTodayInTZ } from "../BalanceSheet/utils/format";
import { fetchBriefReport } from "./api";
import { normalizeBriefReport } from "./utils/normalizeBrief";
import { BriefReport, BriefSection, BriefSelection } from "./types";
import { downloadBriefPdf } from "./utils/briefPdf";
import BriefControls from "./components/BriefControls";
import MonthlyBriefSection from "./components/MonthlyBriefSection";
import BriefPrintable from "./components/BriefPrintable";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Selection from the URL (?mode=&year=&month= or ?mode=custom&start=&end=), default: this month. */
const selectionFromParams = (params: URLSearchParams): BriefSelection => {
  const today = getTodayInTZ();
  const mode = params.get("mode");
  if (mode === "custom") {
    const start = params.get("start") ?? "";
    const end = params.get("end") ?? "";
    if (DATE_RE.test(start) && DATE_RE.test(end)) return { mode, startDate: start, endDate: end };
  }
  const year = Number(params.get("year"));
  const month = Number(params.get("month"));
  const valid = year >= 2000 && month >= 1 && month <= 12;
  return {
    mode: mode === "three_months" ? "three_months" : "monthly",
    year: valid ? year : today.year,
    month: valid ? month : today.month,
  };
};

const selectionToParams = (s: BriefSelection): Record<string, string> =>
  s.mode === "custom" ? { mode: s.mode, start: s.startDate, end: s.endDate } : { mode: s.mode, year: String(s.year), month: String(s.month) };

const selectionKey = (s: BriefSelection) => JSON.stringify(selectionToParams(s));

const MonthlyBriefReport: React.FC = () => {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showErrorMessage: showError } = useNotifier();

  const [selection, setSelection] = useState<BriefSelection>(() => selectionFromParams(searchParams));
  const [report, setReport] = useState<{ key: string; data: BriefReport } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const latestRequest = useRef(0);

  const [drilldown, setDrilldown] = useState<{ section: BriefSection; target: DrilldownTarget; amount: number } | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [printRequest, setPrintRequest] = useState(0);

  const key = selectionKey(selection);
  const invalidRange =
    selection.mode === "custom" &&
    (!DATE_RE.test(selection.startDate) || !DATE_RE.test(selection.endDate) || selection.endDate < selection.startDate);

  useEffect(() => {
    if (invalidRange) return;
    // Only the newest request may update the page.
    const requestId = ++latestRequest.current;
    setLoading(true);
    setLoadError(null);
    fetchBriefReport(projectId, selection)
      .then((api) => {
        if (requestId === latestRequest.current) setReport({ key, data: normalizeBriefReport(api) });
      })
      .catch((error) => {
        if (requestId === latestRequest.current) setLoadError(apiErrorMessage(error, "Failed to load the Monthly Brief Report."));
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, key, reloadKey, invalidRange]);

  useEffect(() => {
    if (!printRequest) return;
    const timer = window.setTimeout(() => window.print(), 100); // let the printed copy render first
    return () => window.clearTimeout(timer);
  }, [printRequest]);

  const changeSelection = (next: BriefSelection) => {
    setSelection(next);
    setSearchParams(selectionToParams(next), { replace: true });
  };

  // Never show one period's figures under another period's controls.
  const current = report && report.key === key ? report.data : null;

  const openDrilldown = (section: BriefSection, row: BreakdownRow) =>
    setDrilldown({ section, target: { kind: row.kind, id: row.id, name: `${row.name} · ${section.label}` }, amount: row.amount });

  // Drill-down is limited to the clicked section's exact dates.
  const drilldownPeriod = useMemo<PeriodSelection>(
    () => (drilldown ? { mode: "range", startDate: drilldown.section.startDate, endDate: drilldown.section.endDate } : { mode: "range", startDate: "", endDate: "" }),
    [drilldown]
  );
  const drilldownTarget = useMemo(() => drilldown?.target ?? null, [drilldown]);

  const handlePdf = () => {
    if (!current) return;
    try {
      downloadBriefPdf(current);
    } catch (error) {
      console.error("Failed to export brief PDF", error);
      showError("Failed to export PDF.");
    }
  };

  return (
    <>
      {/* Header: Project / Monthly Brief Report / period */}
      <Grid container spacing={2} alignItems="center" sx={{ mt: 1, mb: 2 }}>
        <Grid item>
          <IconButton aria-label="Back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs>
          <Typography variant="body2" color="text.secondary">
            {current?.project.name ?? " "}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            Monthly Brief Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current?.label ?? " "}
          </Typography>
        </Grid>
        <Grid item sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" color="error" startIcon={<PictureAsPdfRoundedIcon />} disabled={!current} onClick={handlePdf}>
            Download Brief PDF
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} disabled={!current} onClick={() => setPrintRequest((n) => n + 1)}>
            Print Brief Report
          </Button>
          <Button variant="text" startIcon={<AccountBalanceIcon />} onClick={() => navigate(`/balance-sheet/${projectId}`)}>
            Balance Sheet
          </Button>
        </Grid>
      </Grid>

      <BriefControls selection={selection} onChange={changeSelection} />

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {invalidRange && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Choose a start date and an end date on or after it.
        </Alert>
      )}
      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {current ? (
        current.sections.map((section) => <MonthlyBriefSection key={section.startDate} section={section} onRowClick={openDrilldown} />)
      ) : (
        <Box>
          <Skeleton variant="text" width={220} height={36} />
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rounded" height={72} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            {[0, 1].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Skeleton variant="rounded" height={220} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <TransactionDrilldownDialog
        projectId={projectId}
        period={drilldownPeriod}
        periodLabel={drilldown ? `${drilldown.section.startDate} to ${drilldown.section.endDate}` : ""}
        target={drilldownTarget}
        breakdownTotal={drilldown?.amount ?? 0}
        onClose={() => setDrilldown(null)}
        onOpenFiles={setFiles}
      />
      <AttachmentViewer files={files} onClose={() => setFiles([])} />

      {current && <BriefPrintable report={current} />}
    </>
  );
};

export default MonthlyBriefReport;
